import React, { useState, useEffect } from "react";
import { Check, Crown, HelpCircle, Palette, Save, LayoutGrid, Monitor, Shield, Sparkles, RefreshCw, Upload, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api } from "@/lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";

export type LayoutTemplate = "classic" | "brutalist" | "minimal" | "grid";

interface StatusPageDesignerProps {
  websiteId?: string; // Optional: If passed, customize specifically for a website. Otherwise, custom preset configuration.
  isPro?: boolean;
  brandName?: string;
  tagline?: string;
}

const PRESET_PALETTES = [
  { name: "Default Black", color: "#000000" },
  { name: "Neon Rose", color: "#ec4899" },
  { name: "Matrix Green", color: "#22c55e" },
  { name: "Cyberpunk Gold", color: "#eab308" },
  { name: "Deep Ocean", color: "#3b82f6" },
  { name: "Vibrant Orange", color: "#f97316" },
  { name: "Poison Purple", color: "#a855f7" },
  { name: "Brutal Red", color: "#ef4444" },
];

function compressImage(file: File, maxDimension = 800, quality = 0.75): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxDimension) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get 2D context"));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(compressedDataUrl);
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
}

export function StatusPageDesigner({ websiteId, isPro = false, brandName: propBrandName, tagline: propTagline }: StatusPageDesignerProps) {
  const queryClient = useQueryClient();

  // Settings states
  const [logoUrl, setLogoUrl] = useState("");
  const [accentColor, setAccentColor] = useState("#000000");
  const [layout, setLayout] = useState<LayoutTemplate>("classic");
  const [usePreset, setUsePreset] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [profilePreset, setProfilePreset] = useState<{
    logoUrl: string;
    accentColor: string;
    layout: LayoutTemplate;
  } | null>(null);

  const handleCompression = async (file: File) => {
    setUploading(true);
    try {
      const compressedData = await compressImage(file);
      setLogoUrl(compressedData);
      toast.success("Logo compressed and uploaded successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to compress image.");
    } finally {
      setUploading(false);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image is too large. Max size limit for auto-compression is 5 MB.");
      return;
    }

    if (file.size > 100 * 1024) {
      toast("Image exceeds 100 KB limit", {
        description: "Looks like it's more than 100KB. Do you want to automatically compress and upload it?",
        action: {
          label: "Compress & Upload",
          onClick: () => handleCompression(file),
        },
        duration: 8000,
      });
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoUrl(reader.result as string);
      setUploading(false);
      toast.success("Logo uploaded and encoded successfully!");
    };
    reader.onerror = () => {
      toast.error("Failed to read file.");
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  // Local state for rendering/preview names
  const [dbBrandName, setDbBrandName] = useState("Acme Corp");
  const [dbTagline, setDbTagline] = useState("All Systems Operational");

  const brandName = propBrandName !== undefined ? propBrandName : dbBrandName;
  const tagline = propTagline !== undefined ? propTagline : dbTagline;

  // Fetch initial configuration
  useEffect(() => {
    async function loadConfig() {
      try {
        const profile = await api.getProfile();
        setDbBrandName(profile.white_label_name || "Acme Corp");
        setDbTagline(profile.white_label_tagline || "All Systems Operational");

        const pPreset = {
          logoUrl: profile.status_page_logo_url || "",
          accentColor: profile.status_page_accent_color || "#000000",
          layout: (profile.status_page_layout as LayoutTemplate) || "classic",
        };
        setProfilePreset(pPreset);

        if (websiteId) {
          // Fetch specific website branding
          const site = await api.getSite(websiteId);
          if (site) {
            setUsePreset(site.use_profile_branding_preset !== false);
            setLogoUrl(site.status_page_logo_url || "");
            setAccentColor(site.status_page_accent_color || "#000000");
            setLayout((site.status_page_layout as LayoutTemplate) || "classic");
          }
        } else {
          // Fetch profile preset default branding
          setLogoUrl(profile.status_page_logo_url || "");
          setAccentColor(profile.status_page_accent_color || "#000000");
          setLayout((profile.status_page_layout as LayoutTemplate) || "classic");
        }
      } catch (err) {
        console.error("Failed to load branding configs", err);
      }
    }
    loadConfig();
  }, [websiteId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (websiteId) {
        // Save to specific website metadata
        await api.updateSite(websiteId, {
          status_page_logo_url: !usePreset ? logoUrl || null : null,
          status_page_accent_color: !usePreset ? accentColor || null : null,
          status_page_layout: usePreset ? null : layout || null,
          use_profile_branding_preset: usePreset,
        });
        toast.success("Website status page layout and styling updated");
        queryClient.invalidateQueries({ queryKey: ["websites"] });
        queryClient.invalidateQueries({ queryKey: ["public_website", websiteId] });
      } else {
        // Save to global Profile presets
        await api.updateProfile({
          status_page_logo_url: logoUrl || null,
          status_page_accent_color: accentColor || null,
          status_page_layout: layout,
        });
        toast.success("Global layout and branding template saved to profile");
        queryClient.invalidateQueries({ queryKey: ["profile"] });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      toast.error(err.message || "Failed to save branding parameters");
    } finally {
      setSaving(false);
    }
  };

  const currentLogo = (websiteId && usePreset && profilePreset) ? profilePreset.logoUrl : (logoUrl || "");
  const currentAccent = (websiteId && usePreset && profilePreset) ? profilePreset.accentColor : (accentColor || "#000000");
  const currentLayout = (websiteId && usePreset && profilePreset) ? profilePreset.layout : layout;

  return (
    <div className="space-y-6">
      {/* Scope Selector */}
      {websiteId && (
        <div className="flex items-center justify-between border-4 border-foreground p-4 bg-muted/40 shadow-brutal-sm">
          <div>
            <Label className="text-[10px] font-black uppercase tracking-widest block mb-0.5">Use Global Custom Branding Preset</Label>
            <p className="text-[9px] font-bold text-muted-foreground uppercase">
              Apply layout templates and colors saved in your general settings.
            </p>
          </div>
          <Switch
            checked={usePreset}
            onCheckedChange={(checked) => setUsePreset(checked)}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Editor Controls */}
        <div className={`lg:col-span-6 space-y-6 ${websiteId && usePreset ? "opacity-45 pointer-events-none select-none" : ""}`}>
          {/* Layout Choice */}
          <div className="space-y-3">
            <Label className="text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
              <LayoutGrid className="h-4.5 w-4.5 text-primary" /> Choose Layout Template
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {(["classic", "brutalist", "minimal", "grid"] as LayoutTemplate[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setLayout(t)}
                  type="button"
                  className={`border-2 p-3 text-left transition-all flex flex-col justify-between h-24 relative overflow-hidden ${
                    layout === t
                      ? "border-foreground bg-accent/40 shadow-brutal-xs"
                      : "border-muted-foreground/30 hover:border-foreground/60 bg-background"
                  }`}
                >
                  <span className="text-[10px] font-black uppercase tracking-widest block">{t}</span>
                  <span className="text-[8px] font-bold uppercase text-muted-foreground">
                    {t === "classic" && "Structured & Neat"}
                    {t === "brutalist" && "Heavy borders, raw UI"}
                    {t === "minimal" && "Borderless & clean"}
                    {t === "grid" && "Card-based dashboard"}
                  </span>
                  {layout === t && (
                    <div className="absolute right-1 top-1 bg-foreground text-background p-0.5 border border-foreground">
                      <Check className="h-3 w-3" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Logo URL Input */}
          <div className="space-y-2.5">
            <Label className="text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
              <ImageIcon className="h-4.5 w-4.5 text-primary" /> Logo / Brand Avatar
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {logoUrl ? (
                <div className="border-2 border-foreground p-3 bg-muted/10 flex flex-col items-center justify-center text-center space-y-2">
                  <img src={logoUrl} alt="Logo preview" className="h-10 object-contain max-w-full" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  <button
                    type="button"
                    onClick={() => {
                      setLogoUrl("");
                      toast.info("Logo removed. Remember to save to deploy changes.");
                    }}
                    className="text-[9px] font-black uppercase tracking-wider text-destructive hover:underline"
                  >
                    Remove Logo
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-foreground/30 p-4 bg-muted/10 flex flex-col items-center justify-center text-center space-y-2 relative">
                  <Upload className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <span className="text-[10px] font-black uppercase block cursor-pointer text-primary hover:underline">
                      {uploading ? "Uploading..." : "Upload Image file"}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="logo-file-input-designer"
                      />
                    </span>
                    <span className="text-[8px] font-bold text-muted-foreground uppercase block mt-1">max size 100kb (PNG, JPG, SVG)</span>
                  </div>
                  <label htmlFor="logo-file-input-designer" className="absolute inset-0 cursor-pointer" />
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">Or Paste Image URL</Label>
                <Input
                  placeholder="https://example.com/logo.png"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  className="h-11 rounded-none border-2 border-foreground bg-background text-xs font-bold"
                />
              </div>
            </div>
            <p className="text-[8px] font-bold text-muted-foreground uppercase">
              Please keep the image under 100 KB to save space and ensure maximum loading performance on edge networks.
            </p>
          </div>

          {/* Color Picker */}
          <div className="space-y-3">
            <Label className="text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
              <Palette className="h-4.5 w-4.5 text-primary" /> Accent Palette Selection
            </Label>
            
            <div className="flex gap-2 items-center">
              <Input
                type="color"
                value={currentAccent}
                onChange={(e) => setAccentColor(e.target.value)}
                className="h-11 w-14 p-1 cursor-pointer border-2 border-foreground rounded-none bg-background shrink-0"
              />
              <Input
                type="text"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                placeholder="#000000"
                maxLength={7}
                className="h-11 rounded-none border-2 border-foreground bg-background text-xs font-mono uppercase"
              />
            </div>

            <div className="grid grid-cols-4 gap-2 pt-1.5">
              {PRESET_PALETTES.map((p) => (
                <button
                  key={p.color}
                  onClick={() => setAccentColor(p.color)}
                  type="button"
                  className="h-8 border-2 border-foreground relative flex items-center justify-center hover:scale-105 active:scale-95 transition-all shadow-[1px_1px_0_0_#000]"
                  style={{ backgroundColor: p.color }}
                  title={p.name}
                >
                  {currentAccent.toLowerCase() === p.color.toLowerCase() && (
                    <div className="bg-background border border-foreground p-0.5 rounded-none">
                      <Check className="h-2.5 w-2.5 text-foreground" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Live Preview Pane */}
        <div className="lg:col-span-6 space-y-3">
          <Label className="text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
            <Monitor className="h-4.5 w-4.5 text-primary" /> Live Realtime Preview (Mockup)
          </Label>
          <div className="border-4 border-foreground p-5 bg-background shadow-brutal min-h-[300px] flex flex-col justify-between relative overflow-hidden">
            {/* Template Class Rendering Preview */}
            <div className="space-y-6">
              {/* Brutalist Header Mockup */}
              {currentLayout === "brutalist" && (
                <div className="border-4 border-foreground p-4 uppercase tracking-tighter shadow-brutal-sm flex items-center justify-between" style={{ borderColor: currentAccent }}>
                  <div className="flex items-center gap-3">
                    {currentLogo ? (
                      <img src={currentLogo} alt="Logo" className="h-8 w-8 object-contain border-2 border-foreground" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    ) : (
                      <div className="h-8 w-8 border-2 border-foreground text-xs font-black flex items-center justify-center bg-foreground text-background">
                        {brandName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h4 className="text-sm font-black leading-none">{brandName}</h4>
                      <p className="text-[8px] font-bold text-muted-foreground mt-0.5">{tagline}</p>
                    </div>
                  </div>
                  <span className="text-[8px] font-black border-2 border-foreground px-2 py-0.5 text-background" style={{ backgroundColor: currentAccent }}>ACTIVE</span>
                </div>
              )}

              {/* Classic Layout Preview */}
              {currentLayout === "classic" && (
                <div className="flex items-center justify-between pb-3 border-b-2 border-muted">
                  <div className="flex items-center gap-3">
                    {currentLogo ? (
                      <img src={currentLogo} alt="Logo" className="h-8 w-8 object-contain" />
                    ) : (
                      <div className="h-8 w-8 rounded-none border border-foreground flex items-center justify-center text-xs font-black" style={{ backgroundColor: currentAccent, color: '#fff' }}>
                        {brandName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider leading-none">{brandName}</h4>
                      <p className="text-[8px] font-medium text-muted-foreground uppercase tracking-widest mt-1">{tagline}</p>
                    </div>
                  </div>
                  <span className="text-[8px] font-bold uppercase text-muted-foreground">Global Edge</span>
                </div>
              )}

              {/* Minimal Layout Preview */}
              {currentLayout === "minimal" && (
                <div className="text-center py-4 space-y-2">
                  {currentLogo ? (
                    <img src={currentLogo} alt="Logo" className="h-10 w-10 object-contain mx-auto" />
                  ) : (
                    <div className="h-8 w-8 mx-auto flex items-center justify-center text-sm font-extrabold uppercase" style={{ color: currentAccent }}>
                      {brandName}
                    </div>
                  )}
                  <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest">{tagline}</p>
                </div>
              )}

              {/* Grid Layout Preview */}
              {currentLayout === "grid" && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full" style={{ backgroundColor: currentAccent }} />
                    <span className="text-[10px] font-black uppercase">{brandName}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[1, 2].map((i) => (
                      <div key={i} className="border border-foreground/30 p-2 text-left bg-muted/20">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-[8px] font-black uppercase">Website {i}</span>
                          <span className="h-2 w-2 rounded-full bg-green-500" />
                        </div>
                        <div className="h-1.5 w-full bg-green-500/20" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Mock status panel block */}
              {currentLayout !== "grid" && (
                <div className={`p-4 border-2 ${currentLayout === 'brutalist' ? 'border-foreground shadow-brutal-sm' : 'border-muted'} bg-background`}>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-[9px] font-black uppercase">Core API Endpoint</span>
                    <span className="text-[8px] font-black bg-green-500 text-white px-2 py-0.5 rounded-none uppercase">UP</span>
                  </div>
                  <div className="h-6 w-full bg-muted/40 flex items-center px-2">
                    <div className="h-1 w-full bg-green-500" />
                  </div>
                </div>
              )}
            </div>

            <div className="text-center pt-6 text-[8px] font-bold text-muted-foreground uppercase border-t border-muted/30">
              UptimeWing Whitelabel Sentinel
            </div>
          </div>
        </div>
      </div>

      {/* Deploy Actions */}
      <Button
        onClick={handleSave}
        disabled={saving || (websiteId && usePreset)}
        className="w-full h-12 rounded-none border-2 border-foreground bg-foreground text-background font-black uppercase tracking-widest hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all shadow-brutal-sm"
      >
        {saved ? (
          <span className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Branding Rules Deployed</span>
        ) : saving ? (
          "Saving configurations..."
        ) : (
          <span className="flex items-center gap-2"><Save className="h-4 w-4" /> Save & Deploy Custom Styling</span>
        )}
      </Button>
    </div>
  );
}
